<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { RouteLocation, useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { createContact } from '@/api/contacts';
import GlobalErrorNotification from '@/components/base/GlobalErrorNotification.vue';
import Modal from '@/components/base/layout/Modal.vue';
import TextButton from '@/components/base/TextButton.vue';
import WindowObserver from '@/components/base/WindowObserver.vue';
import AddressForm from '@/components/checkout/AddressForm.vue';
import DeliveryOrPickup from '@/components/checkout/cxMode/DeliveryOrPickup.vue';
import PickupInfoForm from '@/components/checkout/cxMode/PickupInfoForm.vue';
import GuestEmailEntry from '@/components/checkout/GuestEmailEntry.vue';
import ChangeAddress from '@/components/checkout/steps/address/ChangeAddress.vue';
import CountryRestrictedItemsNotification from '@/components/checkout/steps/address/CountryRestrictedItemsNotification.vue';
import GiftRecipientForm from '@/components/checkout/steps/address/GiftRecipientForm.vue';
import MultiShip from '@/components/checkout/steps/multiship/MultiShip.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { useDelivery } from '@/composables/useDelivery';
import { useAddressFields } from '@/composables/useForm';
import { NutsLineItem } from '@/lib/cart/lineItem';
import { useCustomer } from '@/stores/customer';
import { NutsAddress } from '@/utils/address';
import { CheckoutEvents } from '@/utils/analytics';
import {
  containsGiftCertificate,
  hasOnlyGiftCertificate,
  isGiftCertificateLineItem,
  isPresetDeliveryLineItem,
  needPhysicalAddress,
} from '@/utils/cart';

const emit = defineEmits<{ 'step-complete': [] }>();

// eslint-disable-next-line vue/no-reserved-component-names
const route = useRoute();
const router = useRouter();
const store = useStore();

const { initializeAddress } = useAddressFields();
const { customerEmail, lineItems, setCustomerEmail } = useCart(store);
const { addAddress } = useCustomer();
const { addresses, customer } = storeToRefs(useCustomer());
const { cxMode, isB2B, resetCheckout, restrictedCountrySkus } = useCheckout(store);
const {
  hasMultipleShipments,
  isPickup,
  setShippingAddress,
  standardShipments,
  updateRemainingQuantity,
} = useDelivery(store);

const lineItemsContainGiftCertificate = computed(() => containsGiftCertificate(lineItems.value));

const address = ref(
  initializeAddress(
    (!hasMultipleShipments.value && standardShipments.value[0]?.address) || undefined,
  ),
);
if (lineItemsContainGiftCertificate.value) {
  if (address.value.email === customerEmail.value) {
    address.value = { ...address.value, email: '' };
  }
  if (hasOnlyGiftCertificate(lineItems.value)) {
    address.value = { ...address.value, name: '' };
  }
}
const addressToSubmit = ref<NutsAddress>();
const allRestricted = ref(false);
const currentRestrictedItems = ref<NutsLineItem[]>([]);

const customerHasAddress = computed((): boolean => !!customer.value && addresses.value.length > 0);

const inMultiship = computed(() => route.path.endsWith('/address/multi'));

const isPickupSelected = ref(isPickup.value);

const guestEmail = ref({
  address: customerEmail.value ?? '',
});

const allowPickup = computed(() => {
  const digitalLineItems = lineItems.value.filter((li) => isGiftCertificateLineItem(li));
  return digitalLineItems.length === 0;
});

const moreThanOnePieceInCart = computed(() => {
  const standardLineItems = lineItems.value.filter((l) => !isPresetDeliveryLineItem(l));
  return standardLineItems.length > 1 || standardLineItems[0]?.quantity > 1;
});

const requirePhysicalAddress = computed(() => needPhysicalAddress(lineItems.value));

const pendingAction = ref('');
const addressError = ref<Error>();
const addressErrorElement = ref<HTMLElement>();
const addressErrorIntersection = ref<IntersectionObserverEntry>();

const showRestrictedItemsNotification = ref(false);

const intersectAddressError = (intersectionObserver: IntersectionObserverEntry) => {
  addressErrorIntersection.value = intersectionObserver;
};

const exitAddressError = () => {
  addressErrorIntersection.value = undefined;
};

const containsRestrictedItems = (currentAddress: NutsAddress) => {
  if (currentAddress.country === 'US' || !restrictedCountrySkus.value) return false;

  const restrictedSkus = Object.keys(restrictedCountrySkus.value);

  lineItems.value.forEach((lineItem) => {
    const { sku } = lineItem.variant;
    if (
      restrictedSkus.includes(sku) &&
      restrictedCountrySkus.value[sku].includes(currentAddress.country) &&
      !currentRestrictedItems.value.some((l) => l.id === lineItem.id)
    ) {
      currentRestrictedItems.value.push(lineItem);
    }
  });

  allRestricted.value = lineItems.value.length === currentRestrictedItems.value.length;

  return currentRestrictedItems.value.length > 0;
};

const showRestrictedItems = (updatedAddress: NutsAddress) => {
  addressToSubmit.value = updatedAddress;
  showRestrictedItemsNotification.value = true;
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

const stepComplete = () => {
  emit('step-complete');
};

const completeAddressStep = useCallback(async (finalAddress: NutsAddress) => {
  const b2bSource = cxMode.value
    ? 'cx checkout buying for business'
    : 'checkout buying for business';
  if (customer.value) {
    await addAddress(finalAddress, cxMode.value ? customer.value?.email : undefined);
    createContact({
      email: customer.value.email,
      b2b: isB2B.value,
      subscribed: 'unchecked',
      b2bSource: isB2B.value ? b2bSource : undefined,
    });
  } else if (guestEmail.value.address) {
    createContact({
      email: guestEmail.value.address,
      b2b: isB2B.value,
      subscribed: 'if-new',
      b2bSource: isB2B.value ? b2bSource : undefined,
    });
    await setCustomerEmail(guestEmail.value.address);
  }
  await setShippingAddress(finalAddress);
});

const resetAddressError = () => {
  addressError.value = undefined;
};

const onAddressSubmitted = async (finalAddress: NutsAddress) => {
  resetAddressError();
  if (containsRestrictedItems(finalAddress)) {
    pendingAction.value = 'submit';
    showRestrictedItems(finalAddress);
  } else {
    await completeAddressStep.execute(finalAddress);
    if (!completeAddressStep.error) {
      stepComplete();
    } else {
      addressError.value = completeAddressStep.error;
    }
  }
};

const changeAddress = useCallback(setShippingAddress);

const onChangeAddress = async (finalAddress: NutsAddress | null) => {
  resetAddressError();
  if (finalAddress) {
    if (containsRestrictedItems(finalAddress)) {
      pendingAction.value = 'change';
      showRestrictedItems(finalAddress);
    } else {
      await changeAddress.execute(finalAddress);
      if (!changeAddress.error) {
        stepComplete();
      } else {
        addressError.value = changeAddress.error;
      }
    }
  } else {
    if (containsRestrictedItems(address.value)) {
      showRestrictedItems(address.value);
      return;
    }
    stepComplete();
  }
};

const isPending = computed<boolean>(() => completeAddressStep.isPending || changeAddress.isPending);

const onRemoveRestrictedItems = async () => {
  if (!addressToSubmit.value) return;

  let addressKey = addressToSubmit.value.key;
  if (!addressKey && !inMultiship.value) {
    addressKey = standardShipments.value[0]?.key;
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const item of currentRestrictedItems.value) {
    // eslint-disable-next-line no-await-in-loop
    await updateRemainingQuantity(item.id, 0, addressKey);
  }

  showRestrictedItemsNotification.value = false;
  currentRestrictedItems.value = [];
  if (pendingAction.value === 'submit') {
    onAddressSubmitted(addressToSubmit.value);
  } else if (pendingAction.value === 'change') {
    onChangeAddress(addressToSubmit.value);
  }
};

const onSaveAddress = async (finalAddress: NutsAddress) => {
  const savedAddress = await addAddress(
    finalAddress,
    cxMode.value ? customer.value?.email : undefined,
  );
  onChangeAddress(savedAddress);
};

const onTooltip = (name: string) => {
  CheckoutEvents.option(2, `Tooltip - ${name}`);
};

const onSelectedAddressSuggestion = () => {
  CheckoutEvents.option(2, 'Selected Autocompleted Address');
};

const onCancelledAddressSuggestions = () => {
  CheckoutEvents.option(2, 'Cancelled Address Suggestions');
};

const showSwitchToSingleshipConfirmation = ref(false);
const modeChange = useCallback(resetCheckout);
const switchAddressMode = async (mode: string) => {
  const targetRoute: RouteLocation = { name: 'Checkout', params: { step: 'address' } };
  if (mode === 'multi') {
    targetRoute.params!.stepVariant = 'multi';
  }
  if (!standardShipments.value.length) {
    router.push(targetRoute);
    return;
  }
  await modeChange.execute();
  router.push(targetRoute);
  address.value = initializeAddress();
  guestEmail.value.address = customerEmail.value ?? '';
  showSwitchToSingleshipConfirmation.value = false;
};
const confirmSwitchToSingleship = () => {
  if (!hasMultipleShipments.value) {
    router.push({ name: 'Checkout', params: { step: 'address' } });
    return;
  }
  showSwitchToSingleshipConfirmation.value = true;
};

watch(addressError, () => {
  if (addressError.value) {
    if (!addressErrorIntersection.value) {
      addressErrorElement.value?.scrollIntoView({
        behavior: 'smooth',
      });
    }

    let label = '';
    const error = addressError.value;
    console.error(error);
    if (error instanceof Error) {
      label = error.message;
    } else if (typeof error === 'string') {
      label = error;
    }

    CheckoutEvents.event({ action: 'Address', label, category: 'Checkout Error' });
  }
});
</script>

<template>
  <div :class="{ 'opacity-50 pointer-events-none': isPending }">
    <transition name="fade">
      <CountryRestrictedItemsNotification
        v-if="showRestrictedItemsNotification"
        class="mt-3 md:mt-6"
        :allRestricted
        :restrictedItems="currentRestrictedItems"
        @remove-items="onRemoveRestrictedItems"
      />
    </transition>
    <WindowObserver
      :options="{ rootMargin: '20px', threshold: 1 }"
      @intersect="intersectAddressError"
      @exit-view="exitAddressError"
    >
      <div ref="addressErrorElement">
        <GlobalErrorNotification v-if="addressError" class="mt-4 md:mt-8" />
      </div>
    </WindowObserver>
    <transition name="fade">
      <DeliveryOrPickup
        v-if="cxMode && allowPickup"
        v-model="isPickupSelected"
        :isButtonLoading="isPending"
      />
    </transition>
    <PickupInfoForm
      v-if="isPickupSelected"
      v-model="address"
      class="mt-6 md:mt-8"
      @step-complete="stepComplete"
    />
    <template v-else>
      <transition name="fade" mode="out-in">
        <div v-if="inMultiship">
          <TextButton
            class="inline-block mt-4 md:mt-8"
            :class="{ 'cursor-wait loading': modeChange.isPending }"
            @click="confirmSwitchToSingleship()"
          >
            <template v-if="modeChange.isPending">Loading</template>
            <template v-else> Send to a single address </template>
          </TextButton>
          <MultiShip
            class="mt-4"
            :addresses
            :countryRestrictedItems="restrictedCountrySkus"
            :customer
            @step-complete="stepComplete"
            @tooltip="onTooltip"
            @selected-address-suggestion="onSelectedAddressSuggestion"
            @cancelled-address-suggestions="onCancelledAddressSuggestions"
          />
        </div>
        <div v-else-if="customerHasAddress && requirePhysicalAddress">
          <TextButton
            v-if="moreThanOnePieceInCart"
            class="inline-block mt-4 mb-2 md:mt-8"
            :class="{ 'cursor-wait loading': modeChange.isPending }"
            @click="switchAddressMode('multi')"
          >
            <template v-if="modeChange.isPending">Loading</template>
            <template v-else> Ship to multiple addresses </template>
          </TextButton>
          <ChangeAddress
            class="mt-6 md:mt-8"
            :class="{ 'opacity-50 pointer-events-none': modeChange.isPending }"
            :addresses
            :containsGiftCertificate="lineItemsContainGiftCertificate"
            :isButtonLoading="isPending"
            :shippingAddress="address"
            showMakeDefault
            @change-address="onChangeAddress"
            @save-address="onSaveAddress"
            @tooltip="onTooltip"
            @selected-address-suggestion="onSelectedAddressSuggestion"
            @cancelled-address-suggestions="onCancelledAddressSuggestions"
          />
        </div>
        <AddressForm
          v-else
          v-model="address"
          :class="{
            'mt-6 md:mt-8': customer || cxMode,
            'opacity-50 pointer-events-none': modeChange.isPending,
          }"
          :showAddressForm="requirePhysicalAddress"
          showMakeDefault
          :isButtonLoading="isPending"
          uniqueIdSuffix="address"
          @address-submitted="onAddressSubmitted"
          @tooltip="onTooltip"
          @selected-address-suggestion="onSelectedAddressSuggestion"
          @cancelled-address-suggestions="onCancelledAddressSuggestions"
        >
          <template v-if="!customer && !cxMode" v-slot:guestEmailInput>
            <div v-if="!requirePhysicalAddress" class="mt-6 font-bold md:mt-8">
              Where should we email your receipt?
            </div>
            <div v-else class="mt-4 mb-3 font-bold md:mt-7">Email Address for Order Updates</div>
            <GuestEmailEntry v-model="guestEmail" :class="{ 'mt-3': !requirePhysicalAddress }" />
          </template>
          <template v-slot:shippingAddressLabel>
            <div class="flex justify-between" :class="{ 'mt-6 md:mt-8': !customer }">
              <div class="font-bold">Shipping Address</div>
              <TextButton
                v-if="moreThanOnePieceInCart"
                class="inline-block"
                data-test="ship-to-multiple-addresses"
                :class="{ 'cursor-wait loading': modeChange.isPending }"
                @click="switchAddressMode('multi')"
              >
                <template v-if="modeChange.isPending">Loading</template>
                <template v-else> Ship to multiple addresses </template>
              </TextButton>
            </div>
          </template>
          <template v-if="lineItemsContainGiftCertificate" v-slot:giftRecipient>
            <TextButton
              v-if="moreThanOnePieceInCart && !requirePhysicalAddress"
              class="inline-block"
              :class="{ 'cursor-wait loading': modeChange.isPending }"
              @click="switchAddressMode('multi')"
            >
              <template v-if="modeChange.isPending">Loading</template>
              <template v-else> Ship to multiple addresses </template>
            </TextButton>
            <GiftRecipientForm
              v-model:email="address.email"
              v-model:name="address.name"
              class="mt-6 md:mt-8"
              :showName="hasOnlyGiftCertificate(lineItems)"
            />
          </template>
        </AddressForm>
      </transition>
    </template>
    <Modal
      data-test="confirm-switch-to-single-ship"
      :isLoading="modeChange.isPending"
      :isOpen="showSwitchToSingleshipConfirmation"
      width="md:max-w-sm"
      @handle-cancel="showSwitchToSingleshipConfirmation = false"
      @handle-close="showSwitchToSingleshipConfirmation = false"
      @handle-confirm="switchAddressMode('single')"
    >
      <template v-slot:body>
        <div class="mt-7">
          <strong class="text-base font-bold leading-5">
            If you continue, you will be returned to the beginning of Checkout and the addresses you
            entered will be cleared.
          </strong>
        </div>
      </template>
      <template v-slot:confirm-label>Confirm</template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.loading:after {
  animation: ellipsis steps(4, end) 900ms infinite;
  content: '...';
  display: inline-block;
  letter-spacing: 2px;
  overflow: hidden;
  text-decoration: none;
  vertical-align: bottom;
  width: 0;
}

@keyframes ellipsis {
  to {
    width: 1.25rem;
  }
}
</style>
